import {useQuery} from "@tanstack/react-query"

import {useMutationAdapter} from "../../../shared/hook/QueryCustom"
import {QueryApi} from "./QueryRouter"
import {ChartRequest, Connection, DbPlugin, RunRequest, Type} from "./QueryType"

export function useRouterQueryList(type: Type, plugin: DbPlugin, enabled: boolean = true) {
    return useQuery({
        queryKey: QueryApi.list.key(type, plugin),
        queryFn: () => QueryApi.list.fn(type, plugin),
        enabled,
    })
}

export function useRouterQueryUpdate(type: Type, plugin: DbPlugin, onSuccess?: () => void) {
    return useMutationAdapter({
        mutationFn: QueryApi.update.fn,
        mutationKey: QueryApi.update.key(),
        successKeys: [QueryApi.list.key(type, plugin)],
        onSuccess: onSuccess,
    })
}

export function useRouterQueryDelete(type: Type, plugin: DbPlugin, onSuccess?: () => void) {
    return useMutationAdapter({
        mutationFn: QueryApi.delete.fn,
        mutationKey: QueryApi.delete.key(),
        successKeys: [QueryApi.list.key(type, plugin)],
        onSuccess: onSuccess,
    })
}

export function useRouterQueryCreate(type: Type, plugin: DbPlugin, onSuccess?: () => void) {
    return useMutationAdapter({
        mutationFn: QueryApi.create.fn,
        mutationKey: QueryApi.create.key(),
        successKeys: [QueryApi.list.key(type, plugin)],
        onSuccess: onSuccess,
    })
}

export function useRouterQueryRun(request: RunRequest) {
    const {connection, query, options, queryUuid} = request
    const [queryKey, queryFn] = queryUuid ? [
        QueryApi.template.key(queryUuid),
        () => QueryApi.template.fn({connection, options, queryUuid}),
    ] : [
        QueryApi.console.key(),
        () => QueryApi.console.fn({connection, options, query}),
    ]

    return useQuery({
        queryKey: queryKey, queryFn: queryFn,
        retry: false, refetchOnWindowFocus: false, structuralSharing: false,
    })
}

export function useRouterActivity(connection: Connection) {
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return useQuery({
        queryKey: QueryApi.activity.key(),
        queryFn: () => QueryApi.activity.fn(connection),
        retry: false, refetchOnWindowFocus: true,
    })
}

export function useRouterQueryChart(request: ChartRequest, enabled: boolean = false) {
    return useQuery({
        queryKey: QueryApi.chart.key(request),
        queryFn: () => QueryApi.chart.fn(request),
        retry: false, enabled, refetchOnWindowFocus: false,
    })
}

export function useRouterQueryDatabase(connection: Connection, params: any, enabled: boolean = true) {
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return useQuery({
        queryKey: [...QueryApi.databases.key(connection), params.name],
        queryFn: () => QueryApi.databases.fn({connection, ...params}),
        retry: false, enabled, refetchOnWindowFocus: false,
    })
}

export function useRouterQuerySchemas(connection: Connection, params: any, enabled: boolean = true) {
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return useQuery({
        queryKey: [...QueryApi.schemas.key(connection), params.name],
        queryFn: () => QueryApi.schemas.fn({connection, ...params}),
        retry: false, enabled, refetchOnWindowFocus: false,
    })
}

export function useRouterQueryTables(connection: Connection, params: any, enabled: boolean = true) {
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return useQuery({
        queryKey: [...QueryApi.tables.key(connection), params.name],
        queryFn: () => QueryApi.tables.fn({connection, ...params}),
        retry: false, enabled, refetchOnWindowFocus: false,
    })
}

export function useRouterQueryCancel(refetch: () => void) {
    return useMutationAdapter({
        mutationFn: QueryApi.cancel.fn,
        mutationKey: QueryApi.cancel.key(),
        onSuccess: refetch,
    })
}

export function useRouterQueryTerminate(refetch: () => void) {
    return useMutationAdapter({
        mutationFn: QueryApi.terminate.fn,
        mutationKey: QueryApi.terminate.key(),
        onSuccess: refetch,
    })
}

export function useRouterQueryLog(uuid: string) {
    return useQuery({
        queryKey: QueryApi.getLog.key(uuid),
        queryFn: () => QueryApi.getLog.fn(uuid),
        enabled: true, retry: false, refetchOnWindowFocus: false,
    })
}

export function useRouterQueryLogDelete(uuid: string) {
    return useMutationAdapter({
        mutationFn: QueryApi.deleteLog.fn,
        mutationKey: QueryApi.deleteLog.key(),
        successKeys: [QueryApi.getLog.key(uuid)],
    })
}
